import {
  ClearingSaleBuyerStatus,
  ClearingSaleBuyerStatusOrder,
} from "constants/clearingSaleAttributes";

import { caseInsensitiveCompare } from "lib/compare";

// @typescript-eslint/no-redeclare doesn't exempt a same-named const+type merge
// here, so the value and type are named SortFields/SortField respectively.
export const SortFields = {
  BUYER_NAME: "BUYER_NAME",
  STATUS: "STATUS",
  BIDDER_NUMBER: "BIDDER_NUMBER",
} as const;

export type SortField = (typeof SortFields)[keyof typeof SortFields];

type BuyerId = string;
export type ValuesByBuyerId = Record<BuyerId, readonly string[] | undefined>;
type Comparator = (a: BuyerId, b: BuyerId) => number;

export type BuyerSortData = {
  buyerStatusByBuyerId: Record<BuyerId, ClearingSaleBuyerStatus>;
  bidderNumbersByBuyerId: ValuesByBuyerId;
};

// The lowest value (via caseInsensitiveCompare, which handles both numbers and
// alphanumeric strings) among a buyer's per-sale-lot values, used as their sort key.
function getLowestValue(values: readonly string[]): string | undefined {
  let lowest: string | undefined = undefined;
  for (const value of values) {
    if (lowest === undefined || caseInsensitiveCompare(value, lowest) < 0) {
      lowest = value;
    }
  }
  return lowest;
}

// Precomputed once per sort (rather than inside the comparator, which would
// otherwise redo this work on every one of the O(N log N) pairwise comparisons).
function getLowestValueByBuyerId(
  ids: readonly BuyerId[],
  valuesByBuyerId: ValuesByBuyerId,
): Record<BuyerId, string | undefined> {
  const lowestByBuyerId: Record<BuyerId, string | undefined> = {};
  for (const id of ids) {
    lowestByBuyerId[id] = getLowestValue(valuesByBuyerId[id] ?? []);
  }
  return lowestByBuyerId;
}

// Shared mechanism, not a shared rule: both call sites supply their own data and
// get no decisions from here. If either one needs its own collation, split this
// into two named functions rather than adding a parameter.
function compareByLowestValue(
  ids: readonly BuyerId[],
  valuesByBuyerId: ValuesByBuyerId,
): Comparator {
  const lowestByBuyerId = getLowestValueByBuyerId(ids, valuesByBuyerId);
  // A buyer with no sale lots has no sort key, and sorts last.
  return (a, b) => {
    const lowestA = lowestByBuyerId[a];
    const lowestB = lowestByBuyerId[b];
    if (lowestA === undefined && lowestB === undefined) {
      return 0;
    }
    if (lowestA === undefined) {
      return 1;
    }
    if (lowestB === undefined) {
      return -1;
    }
    return caseInsensitiveCompare(lowestA, lowestB);
  };
}

// Returns null when the incoming order is already correct.
export function getSortComparator(
  sortField: SortField,
  { buyerStatusByBuyerId, bidderNumbersByBuyerId }: BuyerSortData,
  ids: readonly BuyerId[],
): Comparator | null {
  switch (sortField) {
    case SortFields.STATUS:
      return (a, b) =>
        ClearingSaleBuyerStatusOrder.indexOf(buyerStatusByBuyerId[a]) -
        ClearingSaleBuyerStatusOrder.indexOf(buyerStatusByBuyerId[b]);
    case SortFields.BIDDER_NUMBER:
      return compareByLowestValue(ids, bidderNumbersByBuyerId);
    case SortFields.BUYER_NAME:
    default:
      // buyerIds is already alphabetical by buyer name (selectAlphabeticalBuyerIds), no re-sort needed
      return null;
  }
}
